import { Button, Modal, Progress, message, notification, Flex } from 'antd'
import { FileExcelOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { Link } from 'react-router-dom'
import { useAsync } from '../../hooks'
import resolveOrdersRequired from '../../lib/orders'
import useInterval from '../../hooks/useInterval'
import useExportExcel from '../../hooks/useExportExcel'
import { useIsAdmin, useCurrentUser, useIsProvider } from '../../redux/slices/session'
import {
  useReports,
  useFetchReports,
  markReportsSigned,
  markReportsSignedPdfReady,
} from '../../redux/slices/reports'
import { toPath } from '../../routes'
import { signReports, getBatchSignatureStatus } from '../../api/reports'
import { ListError, PageHeader } from '../Common'
import CyclesFilter from '../Common/CyclesFilter'
import usePracticeCycles from '../Common/usePracticeCycles'
import useFilters from '../Cycles/useFilters'

import Filters, { FIELDS_OPTIONS } from '../Forms/Filters'
import api from '../../api'
import Reports from './Reports'
import BatchDownloadButton from './BatchDownloadButton'

const POLL_INTERVAL = 2000

const DEFAULT_FILTERS = {
  signed: FIELDS_OPTIONS.signed[0].id,
  hasOrder: FIELDS_OPTIONS.hasOrder[0].id,
  query: '',
  showInactive: false,
}

const useStyles = createUseStyles({
  // Sits under the download button it is blocking, so the bar explains why
  // that button is disabled.
  signatureProgress: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    maxWidth: 288,
    marginLeft: 'auto',
    marginBottom: 12,
    textAlign: 'right',
  },
  signatureProgressLabel: {
    fontSize: 13,
    color: '#595959',
  },
  signatureProgressFailures: {
    fontSize: 13,
    color: '#cf1322',
  },
})

export default function PracticeReports() {
  const dispatch = useDispatch()
  const classes = useStyles()
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const isAdmin = useIsAdmin()
  const [{ error, clearError }, { practice, pending, practiceId }] =
    usePracticeCycles()
  const ordersRequired = resolveOrdersRequired(practice?.integrationConfig)
  const [officeLocations, setOfficeLocations] = useState([])
  const user = useCurrentUser()
  const isProvider = useIsProvider()
  const [hasInteracted, setHasInteracted] = useState(false)
  const {
    conditions,
    setConditions,
    handleFilterChange,
    handlePageChange,
  } = useFilters(
    {
      ...DEFAULT_FILTERS,
      practiceId,
      medicalProviderId: isProvider && !hasInteracted ? user?.id : undefined,
    },
    { defaultToPrevCycle: 'practitioners' },
  )
  const reportFilters = useMemo(
    () => ({
      q: conditions.query,
      signed: conditions.signed,
      hasOrder: ordersRequired && conditions.hasOrder !== 'all' ? conditions.hasOrder : undefined,
      medicalProviderId: conditions.medicalProviderId,
      deviceTypeId: conditions.deviceTypeId,
      officeLocationId: conditions.officeLocationId,
      showInactive: conditions.showInactive || undefined,
    }),
    [conditions, ordersRequired],
  )

  const { cycleId, page, perPage } = conditions

  const { pending: reportPending, execute: fetchReports } = useAsync(
    useFetchReports(practiceId, cycleId, page, reportFilters, perPage),
    false,
  )

  const reports = useReports(cycleId)

  const [exportingExcel, exportExcel] = useExportExcel(
    `practices/${practiceId}/cycles/${cycleId}/reports.xlsx`,
  )

  // Whole aggregated status for every batch the signed-in user has in flight,
  // as returned by the server on each poll.
  const [signatureStatus, setSignatureStatus] = useState(null)
  // Read inside the poll callback, which useInterval keeps out of the render
  // cycle, so the previous snapshot has to live in a ref rather than in state.
  const previousStatus = useRef(null)
  const failedCount = signatureStatus?.failedIds?.length ?? 0
  // Bumped on completion so the reports table refetches with its own args,
  // which are the ones the pagination is actually rendering with.
  const [refreshToken, setRefreshToken] = useState(0)

  const pollSignatureStatus = async () => {
    try {
      const data = await getBatchSignatureStatus()
      const previous = previousStatus.current
      previousStatus.current = data
      setSignatureStatus(data)

      if (data.processedIds.length > 0) {
        dispatch(markReportsSignedPdfReady(data.processedIds))
      }

      if (previous?.inProgress && !data.inProgress) {
        // The terminal payload is built from the trackers before the server
        // clears them, so it carries the run's complete failure list. Only fall
        // back to the last in-flight snapshot if the field is missing.
        const failures = (data.failedIds ?? previous.failedIds).length

        if (failures > 0) {
          notification.warning({
            message: 'Reports ready',
            description: `${failures} report${
              failures !== 1 ? 's' : ''
            } failed to regenerate. The rest are ready to download.`,
          })
        } else {
          notification.success({
            message: 'Reports ready',
            description: 'PDFs have been generated and are ready to download.',
          })
        }

        setRefreshToken(token => token + 1)
      }
    } catch {
      // silent fail — keep polling on next tick
    }
  }

  const sign = async () => {
    try {
      const res = await signReports(practiceId, selectedRowKeys)
      setIsModalVisible(false)

      const { length } = res.reports
      message.success(`${length} report${length !== 1 ? 's' : ''} signed.`)

      dispatch(markReportsSigned(res.reports))
      setSelectedRowKeys([])
      pollSignatureStatus()
    } catch (e) {
      message.error('Failed to sign reports, please try again.')
    }
  }

  useInterval(
    pollSignatureStatus,
    signatureStatus?.inProgress ? POLL_INTERVAL : null,
  )

  // Batches signed before a reload (or on another tab or device) are only
  // known to the server, so ask once on mount and let the interval take over.
  useEffect(() => {
    pollSignatureStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (practiceId) {
      api.practices.getPracticeOfficeLocations(practiceId).then(data => {
        setOfficeLocations(data)
      })
    }
  }, [practiceId])

  if (cycleId && !pending) {
    return (
      <div>
        <PageHeader
          title="Remote Reports"
          subTitle={isAdmin && practice.name}
          hideSubTitleOnMobile
          extra={[
            selectedRowKeys.length > 0 && (
              <Button key="sign" onClick={() => setIsModalVisible(true)}>
                {`Sign ${selectedRowKeys.length} report${
                  selectedRowKeys.length !== 1 ? 's' : ''
                }`}
              </Button>
            ),
            isAdmin && (
              <Link
                key="upload"
                to={toPath.cycles.cycle.reports.upload({ id: cycleId })}
              >
                <Button>Upload reports</Button>
              </Link>
            ),
            <BatchDownloadButton
              key="download"
              cycleId={cycleId}
              pdfPending={!!signatureStatus?.inProgress}
            />,
          ]}
        />
        {signatureStatus?.inProgress && (
          <div className={classes.signatureProgress}>
            <Progress
              percent={signatureStatus.percentage}
              status={failedCount > 0 ? 'exception' : 'active'}
            />
            <span className={classes.signatureProgressLabel}>
              {`${signatureStatus.processedFiles} / ${signatureStatus.totalFiles} reports processed`}
            </span>
            {failedCount > 0 && (
              <span className={classes.signatureProgressFailures}>
                {`${failedCount} report${
                  failedCount !== 1 ? 's' : ''
                } failed to regenerate.`}
              </span>
            )}
          </div>
        )}
        <Flex gap={10} vertical>
          <CyclesFilter
            practiceId={practiceId}
            onSubmit={handleFilterChange}
            currentCycleId={cycleId}
          />
          <Filters
            defaultValues={DEFAULT_FILTERS}
            currentValues={conditions}
            placeholders={{ query: 'Search by name or device serial number' }}
            onSubmit={({
              query,
              signed,
              hasOrder,
              medicalProviderId,
              deviceTypeId,
              officeLocationId,
              showInactive,
            }) => {
              setHasInteracted(true)
              setConditions({
                cycleId,
                query,
                signed,
                hasOrder,
                medicalProviderId,
                deviceTypeId,
                officeLocationId,
                showInactive,
                page: 1,
              })
            }}
            showDeviceType
            showMedicalProvider
            showSigningStatus
            showSearch
            showInactiveCheck
            showOfficeLocation={practiceId && officeLocations.length > 0}
            showHasOrder={ordersRequired}
          />
        </Flex>
        <Modal
          title="Sign reports"
          open={isModalVisible}
          onOk={sign}
          onCancel={() => setIsModalVisible(false)}
          okText="Sign"
          cancelText="Cancel"
        >
          <p>{`You are about to sign ${
            selectedRowKeys.length !== 1
              ? `a batch of ${selectedRowKeys.length} reports`
              : '1 report'
          }.`}</p>
          <p>Generating the signed PDFs takes a few minutes.</p>
          <p>
            You can follow the progress on this screen. Each report shows an
            hourglass until its signed PDF is ready.
          </p>
        </Modal>
        <Reports
          practiceId={practiceId}
          cycleId={cycleId}
          page={page}
          filters={reportFilters}
          onPageChange={handlePageChange}
          selected={selectedRowKeys}
          onSelect={setSelectedRowKeys}
          fetchReports={fetchReports}
          pendingFetch={reportPending}
          ordersRequired={ordersRequired}
          refreshToken={refreshToken}
        />
        {Boolean(reports.length > 0) && (
          <div>
            <Button
              loading={exportingExcel}
              onClick={exportExcel}
              icon={<FileExcelOutlined />}
            >
              Export this list to Excel
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
    </div>
  )
}
